// Per-page default KPI — the cascade's initial selection. Centralizes what were
// scattered inline `defaultKpi` literals across the page components.
//
// Curated against v2's per-page defaults (v2 `kpi_types_*.is_default`) — backlog #5
// facet B (WP-0, 2026-06-03). v2 page names map 1:1 to these keys. v2 leads all four
// performance/dynamics pages with Sales RoI (NPV); the merged Contribution page with
// Sales; Marketing Invests with Net Spend; Review KPIs with Sales (Actual).
//
// unify-Sales (2026-06-03): the modeled-Sales family now lives under one `sales` root
// (drivers Total/Media), and the raw count family moved to the bare `actual_sales` kpi.
// → ReviewKpis leads with `actual_sales`; Contribution leads per-view via CONTRIBUTION_VIEWS
//   ('sales__media__units' channels / 'sales__total__units' groups — #13 merge).
import type { TimeframePresetKey } from '~/state/useTimeframePresetState'
import type { Facet } from '~/state/useChartKnobsState'
import type { ContributionView } from '~/state/useContributionView'

export const PAGE_DEFAULTS = {
  ReviewKpis: 'actual_sales', // unify-Sales: bare Sales (Actual) on kpi_overall
  MarketingInvests: 'nsp', // v2: Net Spend
  CampaignPerformance: 'sales_roi__nsp__npv', // v2: Sales RoI (NPV)
  // #13 merged Contribution page: this is the channels-view (lead) seed; the page resolves the
  // ACTIVE view's seed from CONTRIBUTION_VIEWS at runtime. Kept here because PAGE_DEFAULTS is
  // the key-type source for PAGE_TIMEFRAME_DEFAULTS and documents each page's lead KPI.
  Contribution: 'sales__media__units',
  Dynamics: 'sales_roi__nsp__npv', // v2: Sales RoI (NPV) — merged Campaign+Multi Dynamics (#14, slice 3)
  MultiDeepDive: 'sales_roi__nsp__npv', // v2: Sales RoI (NPV) (was 'nsp')
} as const

// #13: the merged Contribution page's two decomposition presets (replaces WP-6's per-page
// PAGE_CONTRIB_DECOMP). Fully-encoded defaultKpi seeds ALL cascade axes (leak-guarded,
// WP-6 mechanism); groupByDefault is the friendly dim label (verified live). Both render
// stacked; user knobs / URL still override. Display labels live in ContributionViewToggle.
export const CONTRIBUTION_VIEWS: Record<
  ContributionView,
  { defaultKpi: string; groupByDefault: string; facet: Facet }
> = {
  channels: {
    defaultKpi: 'sales__media__units', // Sales by media channel (the old Marketing page)
    groupByDefault: 'Medium Level 1',
    facet: 'stacked_bar',
  },
  groups: {
    defaultKpi: 'sales__total__units', // Sales Media vs Non-Media (the old Driver page)
    groupByDefault: 'Driven by Group',
    facet: 'stacked_bar',
  },
}

// Per-page default timeframe preset (re-anchored to the today-capped data anchor). Status +
// Contribution lead with a 12-week view; the Campaign/Multi performance pages with 4 weeks
// (matches v2's campaign default). Tunable — edit here. (WP-6.)
export const PAGE_TIMEFRAME_DEFAULTS: Record<keyof typeof PAGE_DEFAULTS, TimeframePresetKey> = {
  ReviewKpis: 'last_12_weeks',
  MarketingInvests: 'last_12_weeks',
  Contribution: 'last_12_weeks',
  CampaignPerformance: 'last_4_weeks',
  Dynamics: 'last_4_weeks',
  MultiDeepDive: 'last_4_weeks',
}
